import {
    Amount,
    DocumentViewer,
    DataTable,
    DataTableCell,
    DataTablePresenter,
    formatDate,
    ITableColumn,
    ProviderClaimPayment,
    ProviderClaimPaymentItemAdjustment,
    ProviderDocumentUrl,
    getAdjustmentCode,
    renderAmount,
    Spinner,
} from '@enter/core';
import { inject, observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { IObservedRootStore } from 'stores';
import * as S from './claim-timeline-table.styles';
import { PaymentItems } from './payment-table';

interface IPaymentDetailProps extends IObservedRootStore {
    payment: ProviderClaimPayment;
}

// Reusable row components
const TextRow = React.memo<{
    label?: string;
    value: string | number | undefined;
}>(({ label, value }) => {
    if (!value) {
        return null;
    }

    return (
        <S.ExpandTR>
            {label && <S.ExpandTH>{label}</S.ExpandTH>}
            <S.ExpandTD>{value}</S.ExpandTD>
        </S.ExpandTR>
    );
});

const DateRow = React.memo<{
    label?: string;
    value: string | undefined;
}>(({ label, value }) => {
    if (!value) {
        return null;
    }

    const date = formatDate(value, 'MMM dd, yyyy');

    return (
        <S.ExpandTR>
            {label && <S.ExpandTH>{label}</S.ExpandTH>}
            <S.ExpandTD>{date}</S.ExpandTD>
        </S.ExpandTR>
    );
});

const AmountRow = React.memo<{
    label?: string;
    value: Amount | undefined;
}>(({ label, value }) => {
    if (!value) {
        return null;
    }

    return (
        <S.ExpandTR>
            {label && <S.ExpandTH>{label}</S.ExpandTH>}
            <S.ExpandTD>{renderAmount(value)}</S.ExpandTD>
        </S.ExpandTR>
    );
});

const adjustmentColumns: ITableColumn<ProviderClaimPaymentItemAdjustment>[] = [
    {
        id: 'AdjustmentGroup',
        Header: 'Group',
        accessor: 'group',
        Cell: DataTableCell('String'),
        disableSortBy: true,
    },
    {
        id: 'AdjustmentReason',
        Header: 'Reason',
        accessor: (adjustment: ProviderClaimPaymentItemAdjustment) =>
            adjustment.reasonCode ? getAdjustmentCode(adjustment.reasonCode) : '',
        Cell: DataTableCell('String'),
        disableSortBy: true,
    },
    {
        id: 'AdjustmentAmount',
        Header: 'Amount',
        accessor: 'amount',
        Cell: DataTableCell('Amount'),
        disableSortBy: true,
    },
    {
        id: 'AdjustmentUnits',
        Header: 'Units',
        accessor: 'units',
        Cell: DataTableCell('String'),
        disableSortBy: true,
    },
];

const Details: React.FunctionComponent<{ payment: ProviderClaimPayment }> = React.memo(({ payment }) => {
    return (
        <S.ExpandContainer>
            <S.ExpandTable>
                <S.ExpandTBody>
                    <TextRow label="ID" value={payment.id} />
                    <DateRow label="Payment Date" value={payment.paymentDate} />
                    <DateRow label="Received Date" value={payment.receivedDate} />
                    <TextRow label="Memo" value={payment.memo} />
                    <TextRow label="Payer Control Number" value={payment.payerControlNumber} />
                    <TextRow label="Status" value={payment.status} />
                    <AmountRow label="Charge Amount" value={payment.chargeAmount} />
                    <AmountRow label="Allowed Amount" value={payment.allowedAmount} />
                    <AmountRow label="Payer Amount" value={payment.payerAmount} />
                    <AmountRow label="Patient Amount" value={payment.patientAmount} />
                </S.ExpandTBody>
            </S.ExpandTable>
        </S.ExpandContainer>
    );
});

export const PaymentDetail: React.FunctionComponent<IPaymentDetailProps> = inject('rootStore')(
    observer((props: IPaymentDetailProps) => {
        const { payment, rootStore } = props;
        if (!rootStore) {
            return <></>;
        }

        const [loading, setLoading] = useState(false);
        const [documentUrl, setDocumentUrl] = useState<ProviderDocumentUrl | undefined>();
        const { current: adjustmentsPresenter } = useRef(
            new DataTablePresenter<ProviderClaimPaymentItemAdjustment>({
                name: 'provider-payment-adjustments',
                data: payment.adjustments || [],
                columns: adjustmentColumns,
                filterSchemas: [],
            }),
        );

        useEffect(() => {
            (async (): Promise<void> => {
                if (!payment.id) {
                    return;
                }

                setLoading(true);

                const url = await rootStore.claimStore.loadPaymentDownloadUrl(payment.id);
                setDocumentUrl(url);

                setLoading(false);
            })();
        }, [payment]);

        const renderDocument = () => {
            if (documentUrl?.url) {
                return <DocumentViewer url={documentUrl.url} />;
            }
        };

        adjustmentsPresenter.onDataUpdate(payment.adjustments || []);

        return (
            <S.ExpandWrap key={payment.id}>
                {loading ? (
                    <Spinner />
                ) : (
                    <S.ExpandContainer>
                        <Details payment={payment} />
                        {!!payment.adjustments?.length && (
                            <>
                                <div style={{ fontWeight: 500, marginTop: 8, marginBottom: 4 }}>Adjustments</div>
                                <DataTable
                                    presenter={adjustmentsPresenter}
                                    hidePagination={true}
                                    hideFiltering={true}
                                    isLoading={false}
                                />
                            </>
                        )}
                        {!!payment.items?.length && <PaymentItems items={payment.items} />}
                        {renderDocument()}
                    </S.ExpandContainer>
                )}
            </S.ExpandWrap>
        );
    }),
);
